/**
 * Firm-subdomain — CLIENT-SIDE FORMAT FEEDBACK ONLY.
 *
 * Division of responsibility (see Identity module on the server):
 *  - The company-suffix list, normalization, and uniqueness live on the BACKEND only
 *    (source of truth). Auto-generation is server-driven via `generateSubdomain`.
 *  - This file holds ONLY the "format feedback while typing" mirror — charset + max length —
 *    so inline errors are instant. The server always re-validates on submit; if this mirror
 *    is ever out of step, the only cost is UX, never a bad write.
 *
 * Keep these two values loosely in sync with SubdomainGenerationConstants on the server.
 */

/** Max length. Mirrors SubdomainGenerationConstants.MaxSubdomainLength (format feedback only). */
export const MAX_SUBDOMAIN_LENGTH = 15;

// No /g flag: this regex is module-level and reused across calls. With /g, `.test()` advances
// and persists `lastIndex`, so repeated calls with the same input alternate results — an invalid
// value like 'acme_law' would intermittently report as valid on every other keystroke.
/** Only lowercase letters and digits are allowed in a subdomain. */
const NON_ALPHANUMERIC = /[^a-z0-9]/;

/** True when the value contains only the allowed charset (lowercase letters and digits). */
export function hasOnlyAllowedCharacters(value) {
  return !!value && !NON_ALPHANUMERIC.test(value);
}

/**
 * Validates a (possibly user-edited) subdomain against the format rules for inline feedback.
 * @returns {string|null} an error message, or null when valid.
 */
export function validateSubdomainFormat(value = '') {
  const trimmed = value.trim();
  if (!trimmed) return 'Company subdomain is required';
  // Call out spaces specifically — it's the most common thing users trip on.
  if (/\s/.test(value)) return 'Spaces are not allowed';
  if (!hasOnlyAllowedCharacters(trimmed)) return 'Only lowercase letters and numbers are allowed.';
  if (trimmed.length > MAX_SUBDOMAIN_LENGTH) return `Must be ${MAX_SUBDOMAIN_LENGTH} characters or less.`;
  return null;
}
